import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import logoWebp64 from "@/assets/logo-64.webp";
import logoWebp128 from "@/assets/logo-128.webp";
import logoPng64 from "@/assets/logo-64.png";
import { useLocation, Link } from "react-router-dom"; 

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/pricing", label: "Pricing" },
  {
    href: "/features",
    label: "Features",
    items: [
      { href: "/summaries", label: "Summaries" },
      { href: "/priority-hub", label: "Priority Hub" },
      { href: "/calendar-sync", label: "Calendar Sync" },
      { href: "/smart-faq", label: "Smart FAQ" },
      { href: "/autoreplier", label: "Auto-replier" },
      { href: "/workflows", label: "Workflows" },
    ],
  },
  {
    href: "/integrations",
    label: "Integrations",
    items: [
      { href: "/gmail-integration", label: "Gmail Integration" },
      { href: "/slack-integration", label: "Slack Integration" },
      { href: "/teams-integration", label: "Teams Integration" },
      { href: "/outlook-integration", label: "Outlook Integration" },
      { href: "/instagram-integration", label: "Instagram Integration" },
      { href: "/facebook-integration", label: "Facebook Integration" },
    ],
  },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-background/80 backdrop-blur-xl border-b border-white/5"
          : "bg-transparent"
      }`}
      style={{ transform: "translateZ(0)" }}
    >
      <div className="container">
        <nav className="flex items-center justify-center h-20 relative">
          
          {/* --- LEFT: LOGO --- */}
          <Link to="/" className="absolute left-0 flex items-center gap-2 z-20">
            <picture>
              <source
                srcSet={`${logoWebp64} 1x, ${logoWebp128} 2x`}
                type="image/webp"
              />
              <img
                src={logoPng64}
                alt="ClearMakes"
                width={64}
                height={64}
                className="h-16 w-16 object-contain"
              />
            </picture>
          </Link>

          {/* --- CENTER: Desktop Navigation --- */}
          <div className="hidden md:flex items-center gap-2 z-50">
            {navLinks.map((link) => {
              // Render Dropdown if items exist
              if (link.items) {
                const isParentActive = location.pathname === link.href;
                const isChildActive = link.items.some(
                  (item) => location.pathname === item.href
                );
                const isActive = isParentActive || isChildActive;

                return (
                  <div key={link.label} className="relative group py-4">
                    <div className="relative flex items-center">
                      {isActive && (
                        <motion.div
                          layoutId="activeNavPill"
                          className="absolute inset-0 bg-white/[0.04] rounded-lg border border-white/[0.06]"
                          style={{ zIndex: 0 }}
                          transition={{ type: "spring", stiffness: 300, damping: 35 }}
                        />
                      )}
                      
                      <Link
                        to={link.href!}
                        // FIX 1: Forces the text and arrow to sit entirely in front of the Framer Motion active background pill
                        style={{ transform: "translateZ(1px)" }} 
                        className={`relative z-10 flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 px-4 py-2 ${
                          isActive
                            ? "text-foreground"
                            : "text-muted-foreground hover:text-foreground"
                        }`}
                      >
                        <span>{link.label}</span>
                        
                        {/* FIX 2: Prevents the back of the arrow from being drawn into the background snapshot */}
                        <span 
                          className="flex items-center justify-center transition-transform duration-200 group-hover:rotate-180"
                          style={{ backfaceVisibility: "hidden" }}
                        >
                          <ChevronDown className="h-4 w-4" />
                        </span>
                      </Link>
                    </div>

                    {/* Dropdown Menu */}
                    <div className="absolute left-0 top-full w-56 pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100]">
                      <div className="rounded-xl border border-white/10 bg-[#0A0F1C] backdrop-blur-xl p-2 shadow-2xl flex flex-col gap-1">
                        {link.items.map((subItem) => (
                          <Link
                            key={subItem.href}
                            to={subItem.href}
                            className={`block rounded-md px-3 py-2.5 text-sm transition-colors ${
                              location.pathname === subItem.href
                                ? "bg-white/10 text-white font-semibold"
                                : "text-muted-foreground hover:bg-white/10 hover:text-white"
                            }`}
                          >
                            {subItem.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              // Render Standard Link (Home, Pricing)
              const isActive = location.pathname === link.href;
              return (
                <div key={link.label} className="relative py-4">
                  <div className="relative flex items-center">
                    {isActive && (
                      <motion.div
                        layoutId="activeNavPill"
                        className="absolute inset-0 bg-white/[0.04] rounded-lg border border-white/[0.06]"
                        style={{ zIndex: 0 }}
                        transition={{ type: "spring", stiffness: 300, damping: 35 }}
                      />
                    )}
                    <Link
                      to={link.href!}
                      style={{ transform: "translateZ(1px)" }}
                      className={`relative z-10 flex items-center px-4 py-2 text-sm font-medium transition-colors duration-200 ${
                        isActive
                          ? "text-foreground"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <span>{link.label}</span>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>

          {/* --- RIGHT: Desktop Auth Buttons --- */}
          <div className="hidden md:flex items-center gap-3 absolute right-0" style={{ minWidth: '160px', justifyContent: 'flex-end' }}>
            <Button variant="ghost" size="sm" asChild>
              <a href="/login">Login</a>
            </Button>
            <Button variant="glass" size="sm" asChild>
              <a href="/login?view=signup">Sign Up</a>
            </Button>
          </div>

          {/* --- RIGHT: Mobile Menu Toggle --- */}
          <button
            className="md:hidden p-2 text-foreground absolute right-0"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </div>

      {/* Mobile Menu Dropdown */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background/95 backdrop-blur-xl border-b border-white/5 overflow-hidden"
          >
            <div className="container py-4 space-y-2 max-h-[80vh] overflow-y-auto">
              {navLinks.map((link) => (
                <div key={link.label || link.href}>
                  {link.items ? (
                    <div className="py-1">
                      {/* Dropdown toggle button */}
                      <button
                        type="button"
                        onClick={() => setOpenMobileDropdown(openMobileDropdown === link.label ? null : link.label)}
                        className={`flex items-center justify-between w-full px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                          location.pathname === link.href || link.items.some(i => location.pathname === i.href)
                            ? "bg-white/10 text-white"
                            : "text-foreground hover:bg-white/5"
                        }`}
                      >
                        <span>{link.label}</span>
                        <ChevronDown
                          className={`h-4 w-4 transition-transform duration-200 ${
                            openMobileDropdown === link.label ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      {/* Collapsible subitems */}
                      <AnimatePresence>
                        {openMobileDropdown === link.label && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="overflow-hidden"
                          >
                            <div className="space-y-1 pt-1 pb-2">
                              {link.items.map((subItem) => (
                                <Link
                                  key={subItem.href}
                                  to={subItem.href}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                  className={`block w-full text-left pl-8 pr-4 py-3 text-sm rounded-lg transition-colors ${
                                    location.pathname === subItem.href
                                      ? "bg-white/10 text-white font-medium"
                                      : "text-foreground hover:bg-white/5"
                                  }`}
                                >
                                  {subItem.label}
                                </Link>
                              ))}
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <Link
                      to={link.href!}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`block w-full text-left px-4 py-3 rounded-lg transition-colors font-medium ${
                        location.pathname === link.href
                          ? "bg-white/10 text-white"
                          : "text-foreground hover:bg-white/5"
                      }`}
                    >
                      {link.label}
                    </Link>
                  )}
                </div>
              ))}
              <div className="flex gap-3 pt-4 mt-2 border-t border-white/10 pb-4">
                <Button variant="ghost" className="flex-1" asChild>
                  <a href="/login">Login</a>
                </Button>
                <Button variant="glass" className="flex-1" asChild>
                  <a href="/login?view=signup">Sign Up</a>
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};